import type { AppSettingListItemType } from '@peles/shared';
import { SearchableSelect } from '../../common/SearchableSelect';

export interface ListItemValueInputProps {
  itemType: AppSettingListItemType;
  value: string;
  /** The name shown for a picked person, vendor or venue. */
  label?: string;
  /** A picked record also hands over its name, which becomes the item's label. */
  onChange: (value: string, pickedName?: string) => void;
  id?: string;
  invalid?: boolean;
}

const FIELD =
  'w-full min-h-11 sm:min-h-0 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const ENTITY = { USER: 'user', VENDOR: 'vendor', VENUE: 'venue' } as const;

/** One list item's value, entered the way its list's type asks for. */
export function ListItemValueInput({
  itemType,
  value,
  label,
  onChange,
  id,
  invalid,
}: ListItemValueInputProps) {
  const border = invalid ? 'border-red-400' : 'border-gray-300';
  const common = { id, 'aria-invalid': invalid || undefined, className: `${FIELD} ${border}` };

  switch (itemType) {
    case 'USER':
    case 'VENDOR':
    case 'VENUE':
      return (
        <SearchableSelect
          entityType={ENTITY[itemType]}
          value={value}
          valueLabels={value && label ? { [value]: label } : undefined}
          onChange={(v) => onChange(typeof v === 'string' ? v : (v[0] ?? ''))}
          onResultSelect={(r) => onChange(r.id, r.label)}
          placeholder={`Search ${ENTITY[itemType]}s…`}
        />
      );
    case 'NUMBER':
      return (
        <input
          {...common}
          type="number"
          inputMode="decimal"
          step="any"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'DATE':
      return (
        <input {...common} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
      );
    case 'COLOR':
      return (
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label="Pick a colour"
            value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="h-11 w-14 sm:h-9 shrink-0 rounded border border-gray-300 bg-white p-1"
          />
          <input
            {...common}
            type="text"
            value={value}
            placeholder="#7c3aed"
            onChange={(e) => onChange(e.target.value)}
            className={`${FIELD} ${border} font-mono`}
          />
        </div>
      );
    case 'URL':
      return (
        <input
          {...common}
          type="url"
          inputMode="url"
          value={value}
          placeholder="https://"
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'EMAIL':
      return (
        <input
          {...common}
          type="email"
          inputMode="email"
          value={value}
          placeholder="name@example.com"
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'TEXT':
    default:
      return (
        <input
          {...common}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${FIELD} ${border} font-mono`}
        />
      );
  }
}
